import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Combobox } from "@/components/ui/combobox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { VoiceButton } from "@/components/VoiceButton";
import { ShieldAlert, Plus, Camera, Trash2, Link2, Copy, ArrowRight, ArrowLeft, CheckCircle2, Image as ImageIcon, Mail, Send, User, UserCog, CalendarClock, FileText } from "lucide-react";
import { DateField } from "@/components/ui/date-field";
import { ComplaintShare } from "@/components/ComplaintShare";
import { ComplaintTemplates } from "@/components/ComplaintTemplates";

const STATUS = {
  concept: { l: "Concept", c: "bg-slate-100 text-slate-700" },
  definitief: { l: "Definitief", c: "bg-violet-100 text-violet-700" },
  nieuw: { l: "Nieuw", c: "bg-slate-100 text-slate-700" },
  in_behandeling: { l: "In behandeling", c: "bg-amber-100 text-amber-700" },
  brief_verstuurd: { l: "Doorgezet", c: "bg-blue-100 text-blue-700" },
  afgehandeld: { l: "Afgehandeld", c: "bg-emerald-100 text-emerald-700" },
  afgewezen: { l: "Afgewezen", c: "bg-red-100 text-red-700" },
};
export const CLOSED_COMPLAINT = ["afgehandeld", "afgewezen"];
export function DaysOpenBadge({ c }) {
  if (!c.created_at) return null;
  const closed = CLOSED_COMPLAINT.includes(c.status);
  const end = closed ? new Date(c.closed_at || c.updated_at || Date.now()) : new Date();
  const days = Math.max(0, Math.floor((end - new Date(c.created_at)) / 86400000));
  const cls = closed ? "bg-slate-100 text-slate-600 hover:bg-slate-100" : days > 14 ? "bg-red-100 text-red-700 hover:bg-red-100" : days > 7 ? "bg-amber-100 text-amber-700 hover:bg-amber-100" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-100";
  const d = `${days} ${days === 1 ? "dag" : "dagen"}`;
  return <Badge className={`${cls} font-normal`} data-testid={`complaint-days-open-${c.id}`}>{closed ? `Gesloten na ${d}` : `${d} open`}</Badge>;
}
const adminTranscribe = async (blob) => {
  const fd = new FormData(); fd.append("file", blob, "audio.webm");
  const r = await api.post("/transcribe", fd);
  return r.data.text;
};

export default function Complaints() {
  const [list, setList] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [view, setView] = useState("open");

  const load = () => api.get("/complaints").then((r) => setList(r.data || [])).catch(() => {});
  const openList = list.filter((c) => !CLOSED_COMPLAINT.includes(c.status));
  const archList = list.filter((c) => CLOSED_COMPLAINT.includes(c.status));
  const shown = view === "open" ? openList : archList;
  useEffect(() => { load(); api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {}); }, []);

  return (
    <div className="p-6 space-y-6" data-testid="complaints-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#0F1E3D] flex items-center justify-center text-[#C9A227]"><ShieldAlert className="h-5 w-5" /></div>
          <div>
            <h1 className="font-heading text-2xl font-semibold text-[#0F1E3D]">Klachtenregistratie</h1>
            <p className="text-sm text-slate-500">Registreer klachten over gebreken met foto's en toelichting, of stuur de debiteur een portal-link.</p>
          </div>
        </div>
        <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => setWizardOpen(true)} data-testid="new-complaint-btn"><Plus className="h-4 w-4 mr-2" /> Nieuwe klacht</Button>
      </div>

      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit" data-testid="complaints-tabs">
        {[["open", "Openstaand", openList.length], ["archief", "Archief", archList.length]].map(([v, l, n]) => (
          <button key={v} type="button" onClick={() => setView(v)} data-testid={`complaints-tab-${v}`}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${view === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
            {l} <span className="opacity-60">{n}</span>
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <Card className="p-8 text-center text-slate-500" data-testid="complaints-empty">{view === "open" ? "Geen openstaande klachten." : "Nog geen afgehandelde of afgewezen klachten."}</Card>
      ) : (
        <div className="grid gap-3">
          {shown.map((c) => (
            <Card key={c.id} className="p-4 flex items-center justify-between gap-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setDetail(c)} data-testid={`complaint-row-${c.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-800 truncate">{c.number ? <span className="text-slate-400 font-normal mr-1">{c.number}</span> : null}{c.title}</span>
                  <Badge className={STATUS[c.status]?.c}>{STATUS[c.status]?.l || c.status}</Badge>
                  <DaysOpenBadge c={c} />
                  {c.created_by === "debiteur" || c.via_debtor ? <Badge variant="outline" className="text-xs">via debiteur</Badge> : null}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{c.tenant_name} · {c.photos?.length || 0} foto('s) · {fmtDate(c.created_at)}{c.submitted_at ? ` · ingediend ${fmtDate(c.submitted_at)}` : ""}</div>
              </div>
              <div className="flex items-center gap-2 text-slate-400 shrink-0">
                <ImageIcon className="h-4 w-4" /><span className="text-sm">{c.photos?.length || 0}</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {wizardOpen && <ComplaintWizard tenants={tenants} onClose={() => setWizardOpen(false)} onDone={() => { setWizardOpen(false); load(); }} />}
      {detail && <ComplaintDetail complaint={detail} onClose={() => setDetail(null)} onChanged={() => { load(); api.get(`/complaints/${detail.id}`).then((r) => setDetail(r.data)).catch(() => {}); }} />}
    </div>
  );
}

function PhotoAdder({ onUpload }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const pick = (e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setPreview(URL.createObjectURL(f)); } };
  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try { await onUpload(file, desc); setFile(null); setPreview(""); setDesc(""); }
    finally { setBusy(false); }
  };
  return (
    <div className="border rounded-xl p-3 space-y-2 bg-slate-50/60" data-testid="photo-adder">
      {preview ? <img src={preview} alt="" className="h-40 w-full object-cover rounded-lg" /> : (
        <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-lg cursor-pointer text-slate-400 hover:border-rose-400" data-testid="photo-pick-label">
          <Camera className="h-7 w-7 mb-1" /><span className="text-xs">Foto kiezen / maken</span>
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="photo-file-input" />
        </label>
      )}
      <div className="flex items-start gap-2">
        <Textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Uitleg bij deze foto…" data-testid="photo-desc-input" />
        <VoiceButton transcribe={adminTranscribe} onText={(t) => setDesc((d) => (d ? d + " " : "") + t)} testId="photo-desc-voice" label="" />
      </div>
      <Button className="w-full bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" disabled={!file || busy} onClick={submit} data-testid="photo-add-btn"><Plus className="h-4 w-4 mr-1" /> {busy ? "Uploaden…" : "Foto toevoegen"}</Button>
    </div>
  );
}

function ComplaintWizard({ tenants, onClose, onDone }) {
  const [mode, setMode] = useState("");   // "" | self | debtor
  const [step, setStep] = useState(0);
  const [tenantId, setTenantId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [complaint, setComplaint] = useState(null);
  const [extraNote, setExtraNote] = useState("");
  const [busy, setBusy] = useState(false);

  const tenantOptions = tenants.map((t) => ({ value: t.id, label: `${t.first_name} ${t.last_name}` }));
  const finalDone = (mode === "self" && step === 3) || (mode === "debtor" && step === 1);

  const createComplaint = async (viaDebtor) => {
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/complaints`, { title, description, via_debtor: viaDebtor });
      setComplaint(r.data); setStep(1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Aanmaken mislukt"); }
    finally { setBusy(false); }
  };
  const uploadPhoto = async (file, desc) => {
    const fd = new FormData(); fd.append("file", file); fd.append("description", desc || "");
    const r = await api.post(`/complaints/${complaint.id}/photos`, fd);
    setComplaint((c) => ({ ...c, photos: [...(c.photos || []), r.data] }));
    toast.success("Foto toegevoegd");
  };
  const delPhoto = async (pid) => {
    await api.delete(`/complaints/${complaint.id}/photos/${pid}`);
    setComplaint((c) => ({ ...c, photos: c.photos.filter((p) => p.id !== pid) }));
  };
  const finish = async () => {
    setBusy(true);
    try { await api.patch(`/complaints/${complaint.id}`, { extra_note: extraNote }); setStep(3); }
    catch { toast.error("Opslaan mislukt"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && (finalDone ? onDone() : onClose())}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="complaint-wizard">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-rose-600" /> Nieuwe klacht</DialogTitle></DialogHeader>

        {!mode && (
          <div className="space-y-3" data-testid="complaint-mode-step">
            <p className="text-sm text-slate-500">Wie registreert deze klacht?</p>
            <button onClick={() => setMode("self")} data-testid="complaint-mode-self"
              className="w-full text-left border rounded-xl p-4 flex items-start gap-3 hover:border-rose-400 hover:bg-rose-50/40 transition-colors">
              <div className="h-9 w-9 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center shrink-0"><UserCog className="h-5 w-5" /></div>
              <div><div className="font-medium text-slate-800">Zelf klacht registreren</div><div className="text-xs text-slate-500">U voert de klacht, foto's en toelichting zelf in.</div></div>
            </button>
            <button onClick={() => setMode("debtor")} data-testid="complaint-mode-debtor"
              className="w-full text-left border rounded-xl p-4 flex items-start gap-3 hover:border-rose-400 hover:bg-rose-50/40 transition-colors">
              <div className="h-9 w-9 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center shrink-0"><User className="h-5 w-5" /></div>
              <div><div className="font-medium text-slate-800">Debiteur laat klacht registreren</div><div className="text-xs text-slate-500">Selecteer de debiteur en deel een link via kopiëren, e-mail, SMS of WhatsApp.</div></div>
            </button>
          </div>
        )}

        {mode === "self" && step === 0 && (
          <div className="space-y-4">
            <div>
              <Label>Debiteur</Label>
              <Combobox testId="complaint-tenant-select" value={tenantId} onChange={setTenantId} placeholder="Selecteer debiteur" options={tenantOptions} />
            </div>
            <div><Label>Onderwerp</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="bijv. Lekkage badkamer" data-testid="complaint-title-input" /></div>
            <div>
              <Label>Omschrijving</Label>
              <div className="flex items-start gap-2">
                <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Wat is er aan de hand?" data-testid="complaint-desc-input" />
                <VoiceButton transcribe={adminTranscribe} onText={(t) => setDescription((d) => (d ? d + " " : "") + t)} testId="complaint-desc-voice" label="" />
              </div>
            </div>
            <div className="flex justify-between"><Button variant="outline" onClick={() => setMode("")}><ArrowLeft className="h-4 w-4 mr-2" /> Terug</Button><Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" disabled={!tenantId || !title.trim() || busy} onClick={() => createComplaint(false)} data-testid="complaint-next-1">Volgende <ArrowRight className="h-4 w-4 ml-2" /></Button></div>
          </div>
        )}

        {mode === "self" && step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Voeg foto's toe met per foto een eigen uitleg. Herhaal zo vaak als nodig.</p>
            <PhotoAdder onUpload={uploadPhoto} />
            {complaint?.photos?.length > 0 && (
              <div className="grid grid-cols-3 gap-2" data-testid="complaint-photo-grid">
                {complaint.photos.map((p) => (
                  <div key={p.id} className="relative group">
                    <img src={p.url} alt="" className="h-20 w-full object-cover rounded-lg" />
                    <button className="absolute top-1 right-1 bg-white/90 rounded-full p-1 text-red-600" onClick={() => delPhoto(p.id)} data-testid={`complaint-photo-del-${p.id}`}><Trash2 className="h-3 w-3" /></button>
                    {p.description && <div className="text-[10px] text-slate-500 truncate mt-0.5">{p.description}</div>}
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between"><Button variant="outline" onClick={() => setStep(2)}>Overslaan</Button><Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => setStep(2)} data-testid="complaint-next-2">Volgende <ArrowRight className="h-4 w-4 ml-2" /></Button></div>
          </div>
        )}

        {mode === "self" && step === 2 && (
          <div className="space-y-4">
            <Label>Wil je nog wat toevoegen?</Label>
            <div className="flex items-start gap-2">
              <Textarea rows={4} value={extraNote} onChange={(e) => setExtraNote(e.target.value)} placeholder="Aanvullende opmerkingen…" data-testid="complaint-extra-input" />
              <VoiceButton transcribe={adminTranscribe} onText={(t) => setExtraNote((d) => (d ? d + " " : "") + t)} testId="complaint-extra-voice" label="" />
            </div>
            <div className="flex justify-between"><Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4 mr-2" /> Terug</Button><Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={finish} disabled={busy} data-testid="complaint-finish">Klacht opslaan (Concept)</Button></div>
          </div>
        )}

        {mode === "self" && step === 3 && (
          <div className="space-y-4 py-2" data-testid="complaint-done">
            <div className="text-center"><CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" /><h3 className="font-semibold text-slate-800 mt-1">Klacht geregistreerd als Concept</h3><p className="text-sm text-slate-500">Loop de klacht na en zet deze op Definitief om door te zetten. Eventueel kunt u de debiteur ook zelf laten aanvullen:</p></div>
            <ComplaintShare complaintId={complaint.id} />
            <Button className="w-full bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={onDone} data-testid="complaint-close">Sluiten</Button>
          </div>
        )}

        {mode === "debtor" && step === 0 && (
          <div className="space-y-4" data-testid="complaint-debtor-step">
            <div>
              <Label>Debiteur</Label>
              <Combobox testId="complaint-debtor-tenant-select" value={tenantId} onChange={setTenantId} placeholder="Selecteer debiteur" options={tenantOptions} />
            </div>
            <div>
              <Label>Onderwerp (optioneel)</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="bijv. Lekkage badkamer" data-testid="complaint-debtor-title-input" />
              <p className="text-xs text-slate-400 mt-1">Laat leeg → de debiteur vult het onderwerp zelf in het portaal in.</p>
            </div>
            <div className="flex justify-between"><Button variant="outline" onClick={() => setMode("")}><ArrowLeft className="h-4 w-4 mr-2" /> Terug</Button><Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" disabled={!tenantId || busy} onClick={() => createComplaint(true)} data-testid="complaint-debtor-create">Link aanmaken <ArrowRight className="h-4 w-4 ml-2" /></Button></div>
          </div>
        )}

        {mode === "debtor" && step === 1 && complaint && (
          <div className="space-y-4 py-1" data-testid="complaint-debtor-share">
            <div className="text-center"><CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" /><h3 className="font-semibold text-slate-800 mt-1">Klacht aangemaakt (Concept)</h3><p className="text-sm text-slate-500">Deel de beveiligde link met de debiteur:</p></div>
            <ComplaintShare complaintId={complaint.id} />
            <Button className="w-full bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={onDone} data-testid="complaint-debtor-close">Sluiten</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ComplaintDetail({ complaint, onClose, onChanged }) {
  const [contacts, setContacts] = useState([]);
  const [sel, setSel] = useState({});
  const [subj, setSubj] = useState("");
  const [bodyTxt, setBodyTxt] = useState("");
  const [followup, setFollowup] = useState("");
  const [notifyDebtor, setNotifyDebtor] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [responseDays, setResponseDays] = useState(7);
  const [clientId, setClientId] = useState("");
  const [sending, setSending] = useState(false);
  const todayISO = new Date().toISOString().slice(0, 10);
  const isFinal = complaint.status === "definitief" || complaint.status === "brief_verstuurd";
  useEffect(() => {
    api.get(`/complaints/${complaint.id}/letter-recipients`).then((r) => { setContacts(r.data.contacts || []); setSel(Object.fromEntries((r.data.contacts || []).map((c) => [c.email, true]))); setSubj(`Klacht ${r.data.number || ""} - ${complaint.title}`); setClientId(r.data.client_id || ""); }).catch(() => {});
  }, [complaint.id]);
  const downloadPdf = async () => {
    try {
      const r = await api.get(`/complaints/${complaint.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `klachtdossier_${complaint.number || complaint.id}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("PDF-dossier genereren mislukt"); }
  };
  const sendLetter = async () => {
    const recipients = contacts.filter((c) => sel[c.email]).map((c) => c.email);
    if (!recipients.length) { toast.error("Kies minimaal één contact"); return; }
    setSending(true);
    try { await api.post(`/complaints/${complaint.id}/send-letter`, { recipients, subject: subj, body: bodyTxt, followup_date: followup, notify_debtor: notifyDebtor, response_days: Number(responseDays) || 0 }); toast.success("Klacht doorgezet; status, opvolgdatum en tijdslijn bijgewerkt"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Versturen mislukt"); }
    finally { setSending(false); }
  };
  const setStatus = async (status) => { await api.patch(`/complaints/${complaint.id}`, { status }); toast.success("Status bijgewerkt"); onChanged(); };
  const remove = async () => { if (!window.confirm("Klacht verwijderen?")) return; await api.delete(`/complaints/${complaint.id}`); toast.success("Verwijderd"); onClose(); onChanged(); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="complaint-detail">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-rose-600" /> {complaint.number ? `${complaint.number} · ` : ""}{complaint.title}</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center gap-3 flex-wrap text-sm text-slate-500">
            <span>{complaint.tenant_name}</span>
            <div className="w-44"><Combobox testId="complaint-status-select" value={complaint.status} onChange={setStatus} options={Object.entries(STATUS).map(([k, v]) => ({ value: k, label: v.l }))} /></div>
            {complaint.created_by === "debiteur" || complaint.via_debtor ? <Badge variant="outline" className="text-xs">via debiteur</Badge> : null}
            <Button variant="outline" size="sm" className="ml-auto" onClick={downloadPdf} data-testid="complaint-download-pdf"><FileText className="h-4 w-4 mr-1.5" /> PDF-dossier</Button>
          </div>
          {complaint.description && <p className="text-sm text-slate-700 whitespace-pre-wrap">{complaint.description}</p>}
          {complaint.photos?.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {complaint.photos.map((p) => (
                <a key={p.id} href={p.url} target="_blank" rel="noreferrer" className="block" data-testid={`detail-photo-${p.id}`}>
                  <img src={p.url} alt="" className="h-28 w-full object-cover rounded-lg" />
                  {p.description && <div className="text-xs text-slate-600 mt-1">{p.description}</div>}
                </a>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400">Nog geen foto's.</p>}
          {complaint.extra_note && <div className="text-sm bg-slate-50 border rounded-lg p-3"><span className="text-xs font-semibold text-slate-500">Aanvulling:</span><div className="whitespace-pre-wrap text-slate-700">{complaint.extra_note}</div></div>}
          <div className="pt-2 border-t">
            <button type="button" onClick={() => setShowShare((v) => !v)} className="flex items-center gap-2 text-sm text-slate-600 hover:text-rose-600 transition-colors" data-testid="complaint-toggle-share">
              <Link2 className="h-4 w-4" /> Debiteur zelf laten aanvullen (portal-link delen)
              <span className="text-xs text-slate-400">· {showShare ? "verbergen" : "tonen"}</span>
            </button>
            {showShare && <div className="mt-3"><ComplaintShare complaintId={complaint.id} /></div>}
          </div>
          <div className="pt-3 border-t space-y-2">
            <Label className="text-xs flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> Doorzetten naar assetmanager / opdrachtgever-contact</Label>
            {!isFinal ? (
              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-700 rounded-lg p-2.5" data-testid="complaint-not-final-hint">Zet de klacht eerst op <b>Definitief</b> (statusveld hierboven) om deze te kunnen doorzetten.</div>
            ) : contacts.length === 0 ? <p className="text-xs text-slate-400">Geen contacten met e-mail gevonden bij pand of opdrachtgever.</p> : (
              <div className="space-y-1.5">
                {contacts.map((ct, i) => (
                  <label key={i} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!sel[ct.email]} onChange={() => setSel((s) => ({ ...s, [ct.email]: !s[ct.email] }))} data-testid={`klachtbrief-contact-${i}`} />
                    <span>{ct.name || ct.email} <span className="text-xs text-slate-400">· {ct.source} · {ct.email}</span></span>
                  </label>
                ))}
                <Input value={subj} onChange={(e) => setSubj(e.target.value)} className="mt-1" data-testid="klachtbrief-subject" />
                <Textarea rows={3} value={bodyTxt} onChange={(e) => setBodyTxt(e.target.value)} placeholder="Laat leeg voor standaardtekst…" data-testid="klachtbrief-body" />
                <ComplaintTemplates scope="letter" testId="klachtbrief-templates" clientId={clientId} current={{ body: bodyTxt }} onApply={(t) => { if (t.body) setBodyTxt(t.body); }} />
                <div>
                  <Label className="text-xs flex items-center gap-1 text-slate-600"><CalendarClock className="h-3.5 w-3.5" /> Opvolgdatum (komt in Taken)</Label>
                  <DateField value={followup} onChange={setFollowup} min={todayISO} testId="klachtbrief-followup" />
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1 text-slate-600"><CalendarClock className="h-3.5 w-3.5" /> Auto-herinnering na (dagen zonder terugkoppeling)</Label>
                  <Input type="number" min="0" value={responseDays} onChange={(e) => setResponseDays(e.target.value)} className="w-28" data-testid="klachtbrief-response-days" />
                  <p className="text-[11px] text-slate-400 mt-0.5">0 = geen automatische herinnering. Anders komt er na dit aantal dagen automatisch een opvolgtaak als de klacht nog niet is afgehandeld.</p>
                </div>
                <label className="flex items-center gap-2 text-sm bg-slate-50 border rounded-lg p-2.5">
                  <input type="checkbox" checked={notifyDebtor} onChange={() => setNotifyDebtor((v) => !v)} data-testid="klachtbrief-notify-debtor" />
                  <span>Ook de debiteur <b>{complaint.tenant_name}</b> per e-mail informeren (ter kennisgeving)</span>
                </label>
                <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" disabled={sending} onClick={sendLetter} data-testid="klachtbrief-send"><Send className="h-4 w-4 mr-2" /> {sending ? "Doorzetten…" : "Doorzetten (met PDF-bijlage)"}</Button>
                <p className="text-[11px] text-slate-400">De brief bevat het klachtnummer + foto's als PDF-bijlage. De debiteur krijgt de status "Klachtprocedure", de opvolgdatum komt als taak binnen, en alles wordt in de tijdslijn vastgelegd.</p>
              </div>
            )}
          </div>
          <div className="flex justify-end"><Button variant="outline" className="text-red-600 border-red-200" onClick={remove} data-testid="complaint-delete"><Trash2 className="h-4 w-4 mr-2" /> Verwijderen</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
